/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Switch } from 'antd'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  GameFormMode,
  GameFormValues,
} from '@sportninja/common/features/game/gameForm'
import {
  paragraphSmall,
  paragraphXSmall,
  weightMedium,
  weightRegular,
} from 'src/components/css'
import { TextInput } from 'src/components/ts-components/TextInput'
import { PLACEHOLDER_VISITOR_NAME_MAX_LENGTH } from '@sportninja/common/features/game/placeholderVisitor/placeholderVisitorKeys'
import GameDateTimeFields from './GameDateTimeFields'
import GameSearchField, { type SearchFieldOption } from './GameSearchField'

interface Props {
  /** Create and edit publish the picked instant differently -- see
   *  `GameDateTimeFields`, which is the only consumer. */
  mode: GameFormMode
  values: GameFormValues
  onChange: (next: Partial<GameFormValues>) => void
  hasError: (name: keyof GameFormValues) => boolean
  /** Inline per-field errors from the API, keyed by form field name. */
  fieldErrors: Record<string, string>

  teamOptions: SearchFieldOption[]
  venueOptions: SearchFieldOption[]
  facilityOptions: SearchFieldOption[]
  gameTypeOptions: SearchFieldOption[]
  subCompetitionOptions?: SearchFieldOption[]
  /** What the competition calls its child level — "Division", "Pool", … */
  subCompetitionLabel?: string
  subCompetitionsLoading?: boolean

  teamsLoading?: boolean
  venuesLoading?: boolean
  facilitiesLoading?: boolean

  /** Teams are locked once the game has started or completed. */
  teamsDisabled?: boolean
  /** Competition permits placeholder visitors. */
  canUsePlaceholderVisitor?: boolean
  /** Started/finalized game locks the toggle. */
  isPlaceholderVisitorLocked?: boolean
  hasPlaceholderTeam?: boolean
  /** The "All Locations" scope toggle rendered above the location list. */
  locationScopeToggle?: React.ReactNode
  /** The "Create a new location" row rendered under the location list. */
  createLocationRow?: React.ReactNode
}

/**
 * The game form's field grid. Purely presentational -- it owns no state and
 * performs no I/O, so every field is driven by `values` / `onChange`.
 */
const GameForm: React.FC<Props> = ({
  mode,
  values,
  onChange,
  hasError,
  fieldErrors,
  teamOptions,
  venueOptions,
  facilityOptions,
  gameTypeOptions,
  subCompetitionOptions = [],
  // Legacy's GameScheduleSelect fell back to the competition label, which
  // Canlan renames to "season" -- not a hardcoded "Division".
  subCompetitionLabel = capitalizeWords(t('common:competition', {})),
  subCompetitionsLoading = false,
  teamsLoading = false,
  venuesLoading = false,
  facilitiesLoading = false,
  teamsDisabled = false,
  canUsePlaceholderVisitor = false,
  isPlaceholderVisitorLocked = false,
  hasPlaceholderTeam = false,
  locationScopeToggle,
  createLocationRow,
}) => (
  <div css={formStyle}>
    <GameDateTimeFields
      mode={mode}
      values={values}
      onChange={onChange}
      hasError={hasError}
      fieldErrors={fieldErrors}
    />

    <div css={rowStyle}>
      <GameSearchField
        label={t('Web:homeTeam', {})}
        value={values.homeTeamId}
        // Each side excludes the other's pick, so a team cannot be scheduled
        // against itself. Ported from the legacy GameTeamPicker.
        options={teamOptions.filter(
          (team) => team.value !== values.visitingTeamId
        )}
        onChange={(next) => onChange({ homeTeamId: next })}
        error={fieldErrors.homeTeamId}
        placeholder={t('Web:selectATeam', {})}
        required
        disabled={teamsDisabled}
        loading={teamsLoading}
        hasError={hasError('homeTeamId')}
      />
      {/*
        Replaced by the placeholder name field when the toggle is on: the two
        are mutually exclusive, and a placeholder game has no team to pick.
      */}
      {values.usePlaceholderVisitor ? (
        <div css={fieldStyle}>
          <TextInput
            name='visitingTeamPlaceholder'
            value={values.visitingTeamPlaceholder}
            setValue={(next) => onChange({ visitingTeamPlaceholder: next })}
            label={capitalizeWords('Visiting Team Placeholder Name')}
            placeholder={capitalizeWords('Visiting Team Placeholder Name')}
            fieldErrors={fieldErrors}
            customError={null}
            maxLength={PLACEHOLDER_VISITOR_NAME_MAX_LENGTH}
            /*
             * Locked by the SAME signal as the team pickers: a started or
             * finished game cannot change its visitor, and the name is that
             * visitor (SN-8410).
             */
            disabled={teamsDisabled}
          />
        </div>
      ) : (
        <GameSearchField
          label={t('Web:visitingTeam', {})}
          value={values.visitingTeamId}
          options={teamOptions.filter(
            (team) => team.value !== values.homeTeamId
          )}
          onChange={(next) => onChange({ visitingTeamId: next })}
          error={fieldErrors.visitingTeamId}
          placeholder={t('Web:selectATeam', {})}
          required
          disabled={teamsDisabled}
          loading={teamsLoading}
          hasError={hasError('visitingTeamId')}
        />
      )}
    </div>

    {/*
      Disabled in two independent cases, each saying why rather than
      presenting an unresponsive switch (SN-8410):
      - the competition does not allow placeholder visitors; or
      - the game has started or been finalized, after which the API refuses
        to switch between a placeholder and a real team.
    */}
    <div css={toggleRowStyle}>
      <span css={toggleCopyStyle}>
        <span css={toggleTitleStyle}>
          {capitalizeWords('Placeholder Visiting Team')}
        </span>
        <span css={toggleSubtitleStyle}>
          {!canUsePlaceholderVisitor
            ? 'This competition does not allow placeholder visiting teams.'
            : isPlaceholderVisitorLocked
            ? 'Cannot be changed once the game has started or been finalized.'
            : 'When turned on, enter a placeholder name instead of selecting a visiting team.'}
        </span>
      </span>
      <Switch
        size='small'
        css={switchStyle}
        checked={values.usePlaceholderVisitor}
        disabled={!canUsePlaceholderVisitor || isPlaceholderVisitorLocked}
        onChange={(checked) =>
          onChange(
            checked
              ? { usePlaceholderVisitor: true, visitingTeamId: null }
              : { usePlaceholderVisitor: false, visitingTeamPlaceholder: '' }
          )
        }
        aria-label='Placeholder visiting team'
      />
    </div>

    {/*
      Only surfaced when a PLACEHOLDER team is selected, matching the legacy
      GameScheduleSelect -- and only then is it required.

      A placeholder ("Winner of Semifinal 1") belongs to no division, so the
      API cannot infer which one the game lands in. The label is whatever the
      competition calls its child level, and the note explains why a field
      nobody normally sees has just appeared.
    */}
    {hasPlaceholderTeam && (
      <div css={rowStyle}>
        <GameSearchField
          label={subCompetitionLabel}
          value={values.scheduleId}
          options={subCompetitionOptions}
          onChange={(next) => onChange({ scheduleId: next })}
          error={fieldErrors.scheduleId}
          placeholder={`Select a ${subCompetitionLabel}`}
          required
          loading={subCompetitionsLoading}
          hasError={hasError('scheduleId')}
        />
        {/*
          Two explicit lines rather than one wrapping sentence: the statement
          and the instruction are separate thoughts, and letting them wrap
          wherever the column happens to end reads as a run-on.
        */}
        <span css={placeholderNoteStyle}>
          <span>
            Placeholder teams do not have a {subCompetitionLabel.toLowerCase()}.
          </span>
          <span>Please select one manually.</span>
        </span>
      </div>
    )}

    <div css={rowStyle}>
      <GameSearchField
        label={t('common:location', {})}
        value={values.venueId}
        options={venueOptions}
        onChange={(next) => onChange({ venueId: next })}
        error={fieldErrors.venueId}
        placeholder={t('Web:selectALocation', {})}
        required
        loading={venuesLoading}
        hasError={hasError('venueId')}
        header={locationScopeToggle}
        footer={createLocationRow}
      />
      <GameSearchField
        label={t('common:facility', {})}
        value={values.facilityId}
        options={facilityOptions}
        onChange={(next) => onChange({ facilityId: next })}
        error={fieldErrors.facilityId}
        placeholder={t('Web:selectAFacility', {})}
        // Matches legacy: the facility list is meaningless without a location,
        // and a location may legitimately have none.
        disabled={!values.venueId || facilityOptions.length === 0}
        loading={facilitiesLoading}
      />
    </div>

    <div css={rowStyle}>
      <GameSearchField
        label={t('common:gameType', {})}
        value={values.gameTypeId === null ? null : String(values.gameTypeId)}
        options={gameTypeOptions}
        onChange={(next) => onChange({ gameTypeId: next })}
        error={fieldErrors.gameTypeId}
        placeholder={`Select a ${t('common:gameType', {})}`}
      />
      <div css={fieldStyle}>
        <TextInput
          name='gameNumber'
          value={values.gameNumber}
          setValue={(next) => onChange({ gameNumber: next })}
          label={capitalizeWords(t('common:gameNumber', {}))}
          placeholder='#####'
          fieldErrors={fieldErrors}
          customError={null}
        />
      </div>
    </div>

    <div css={dividerStyle} />

    <div css={toggleRowStyle}>
      <span css={toggleCopyStyle}>
        <span css={toggleTitleStyle}>
          {capitalizeWords(t('Web:ifNecessary', {}))}
        </span>
        <span css={toggleSubtitleStyle}>
          When turned ON, indicate game may not be required based on prior
          results.
        </span>
      </span>
      <Switch
        size='small'
        css={switchStyle}
        checked={values.ifNecessaryStatus}
        onChange={(checked) => onChange({ ifNecessaryStatus: checked })}
        aria-label='If necessary'
      />
    </div>

    <div css={toggleRowStyle}>
      <span css={toggleCopyStyle}>
        <span css={toggleTitleStyle}>
          {capitalizeWords('Upcoming LiveStream')}
        </span>
        <span css={toggleSubtitleStyle}>
          When turned on, indicates the game has an upcoming livestream.
        </span>
      </span>
      <Switch
        size='small'
        css={switchStyle}
        checked={values.isUpcomingLivestream}
        onChange={(checked) => onChange({ isUpcomingLivestream: checked })}
        aria-label='Upcoming livestream'
      />
    </div>
  </div>
)

export default GameForm

// ─── Styles ────────────────────────────────────────────────────────────────

const formStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Even 20px top and bottom: the body's border sits directly under the header,
     and the last toggle needs the same breathing room as the first row. */
  padding: 20px 16px;
`

const rowStyle = css`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

// TextInput renders its label and input as a bare fragment, so the label-to-
// field spacing comes from here. 8px matches GameSearchField and the date/time
// fields, keeping every label in the form the same distance from its control.
const fieldStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

// No extra margin: the form's own 16px gap already separates the divider from
// the rows either side, and adding to it made the space above "If Necessary"
// noticeably larger than the space below the last toggle.
// Sits beside the division select, in the empty half of that row -- bottom
// aligned with the field so it reads as help for it, not as a stray sentence.
/*
 * Anchored to the TOP of the row, not the bottom.
 *
 * Bottom-aligning it tied the note to the row's height, so the moment a
 * validation error appeared under the select the note slid down with it. The
 * offset lines the first line of text up with the control beside it: 20px of
 * label plus the field's 8px gap.
 */
const placeholderNoteStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  display: flex;
  flex-direction: column;
  align-self: start;
  margin-top: 28px;
  color: ${colors.NEUTRAL_50};
`

const dividerStyle = css`
  height: 1px;
  background: ${colors.SOFT_STEEL};
`

const toggleRowStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`

// Grows into the row's free space so the subtitle uses the full width rather
// than wrapping early next to the switch.
const toggleCopyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1 1 auto;
  min-width: 0;
`

// The switch keeps its intrinsic width; only the copy column flexes.
const switchStyle = css`
  flex: 0 0 auto;
`

const toggleTitleStyle = css`
  ${paragraphSmall}
  ${weightMedium}
  color: ${colors.WHITE};
`

const toggleSubtitleStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  color: ${colors.NEUTRAL_50};
`
